import { ListItem, ListItemText } from "@mui/material";
import { getFullName } from "encamp-shared/src/utils/name";
import { getWorkOrFirstPhoneNumber } from "encamp-shared/src/utils/phoneNumber";
import { gql } from "generated-graphql";
import { PersonPickerDetailsFragment, PersonPickerFragment } from "generated-graphql/graphql";
import React, { useMemo } from "react";
import { FieldError } from "react-hook-form";
import { useParams } from "react-router-dom";

import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

gql(`
  fragment PersonPicker on Person {
    id
    first
    last
    email
  }
`);

gql(`
  fragment PersonPickerDetails on Person {
    title
    state
    phones {
      type
      number
    }
  }
`);

const PERSON_QUERY = gql(`
  query People(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $showDetails: Boolean = false
  ) {
    people(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...PersonPicker
        ...PersonPickerDetails @include(if: $showDetails)
      }
      count
    }
  }
`);

const getPersonDisplay = (person: PersonPickerFragment) => {
  if (person.email && person.first) {
    return `${getFullName(person)} <${person.email ?? ""}>`.trim();
  } else if (person.email) {
    return `${person.email ?? ""}`.trim();
  } else {
    return getFullName(person);
  }
};

/** Title, phone, and state, to tell same-named contacts apart. */
const getPersonDetails = (person: PersonPickerDetailsFragment) =>
  [person.title, getWorkOrFirstPhoneNumber(person.phones), person.state]
    .filter(Boolean)
    .join(" · ");

const filterPerson = (person: PersonPickerFragment) => {
  return person.first || person.last || person.email;
};

/** A picked person; the details fields are only fetched when `showDetails` is on. */
export type PersonPickerValue = PersonPickerFragment & PersonPickerDetailsFragment;

type OptionType = PersonPickerValue & {
  inputValue?: string;
};

interface PersonPickerProps {
  label?: string;
  helperText?: (value: PersonPickerValue | null | undefined) => string | undefined;
  onSelectPerson: (person: PersonPickerValue | null) => void;
  value?: PersonPickerValue | null;
  error?: FieldError;
  disabled?: boolean;
  /** Defaults to the tenant in the URL. */
  tenantId?: string;
  /** Offers to add a typed name as a new, unsaved person. */
  allowCreate?: boolean;
  /** Shows each option's title, phone, and state under its name. */
  showDetails?: boolean;
}

export const PersonPicker: React.FC<PersonPickerProps> = ({
  label = "Contact",
  helperText = () => "",
  onSelectPerson,
  value,
  error,
  disabled,
  tenantId: tenantIdProp,
  allowCreate = true,
  showDetails = false,
}) => {
  const { tenantId: tenantIdParam = "" } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdProp ?? tenantIdParam;

  const helperTextResult = useMemo(() => helperText(value), [helperText, value]);

  return (
    <>
      <AsyncAutocomplete
        query={PERSON_QUERY}
        variables={{ showDetails }}
        initialPageSize={25}
        initialSortModel={[
          { field: "last", sort: "asc" },
          { field: "first", sort: "asc" },
        ]}
        additionalSearchString={`tenantId:${tenantId}`}
        getItems={(d) => d.people.items}
        getOptionLabel={(option: OptionType) => getPersonDisplay(option)}
        getOptionKey={(o: OptionType) =>
          o.id || `${o.first ?? ""}|${o.last ?? ""}|${o.email ?? ""}`
        }
        placeholderLabel={label}
        noOptionsText="Search for a person"
        textFieldProps={{
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          },
        }}
        onChange={(newValue: OptionType | null) => {
          if (newValue && newValue.inputValue) {
            const input = newValue.inputValue;
            const stripped = input.replace(/^Add "|"$/g, "");
            const words = stripped.split(" ");
            onSelectPerson({
              id: "",
              first: words[0] ?? stripped,
              last: words.slice(1).join(" "),
            });
          } else {
            onSelectPerson(newValue);
          }
        }}
        value={value}
        error={error}
        helperText={helperTextResult}
        disabled={disabled}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={(options: OptionType[], params: any) => {
          const inputValue = params.inputValue as string;
          const filtered = options
            .filter(filterPerson)
            .filter((o) => getPersonDisplay(o).toLowerCase().includes(inputValue.toLowerCase()));
          const isExisting = filtered.some((option) => inputValue === getPersonDisplay(option));
          if (allowCreate && inputValue !== "" && !isExisting) {
            const words = inputValue.split(" ");
            filtered.push({
              id: `__new__:${inputValue.toLowerCase().trim()}`,
              first: words[0] ?? inputValue,
              last: words.slice(1).join(" "),
              inputValue: `Add "${inputValue}"`,
            });
          }
          return filtered;
        }}
        lazyLoad
        renderOption={
          showDetails
            ? (props, option: OptionType) => (
                <ListItem {...props} key={option.id}>
                  <ListItemText
                    primary={getPersonDisplay(option)}
                    secondary={getPersonDetails(option)}
                  />
                </ListItem>
              )
            : undefined
        }
        onBlur={(e) => {
          if (allowCreate && !value) {
            const target = e.target as HTMLInputElement;
            const words = target?.value?.split(" ") ?? [];
            if (words.length && words[0].length)
              onSelectPerson({
                id: "",
                first: words[0],
                last: words.slice(1).join(" "),
              });
          }
        }}
      />
    </>
  );
};
